// @ts-strict-ignore
import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  NgZone,
  Output,
  ViewChild
} from '@angular/core';
import { LMListComponent, LMListSelectionEvent } from '@list/lm-list.component';
import { LMRouteComponentService } from '@services/lm-route-component.service';
import { AppConstants } from '@utils/app.constants';
import { LMContextService } from '@app/services';
import { LMSelectionEvent } from '@app/components';

@Component({
  template:  `
    <lm-list #lmlistlookup
      [listUrlInfo]="listUrlInfo"
      [listSpec]="listSpec"
      [selectRequestData]="selectRequestData"
      [ngStyle]="{'width': '100%'}"

      (listDisplayed)="onListDisplayed()"
      (listRowClicked)="onRowClicked($event)"
      (listRowSelected)="onRowsSelected($event)"
      (toolbarButtonEvent)="onToolbarButtonEvent($event)"
      (listKeyDown)="onKeyDown($event)"
    >
    </lm-list>
  `,
  providers: [ LMRouteComponentService ]
})
export class LMSelectDialogComponent implements AfterViewChecked, AfterViewInit {

  @ViewChild(forwardRef(() => LMListComponent)) selectList: LMListComponent;

  @HostListener('keyup', [ '$event' ]) onKeyUp(event) {
    if (event.which === 13 && event.srcElement.nodeName !== 'BUTTON' && event.srcElement.nodeName !== 'INPUT') { // 13 => enter
      // Do not perform a row click (which closes the dialog and returns the selected row)
      // for an enter key initiated from the filter row
      if (this.enterKeyFromFilterRow) {
        this.enterKeyFromFilterRow = false;
        return;
      }

      const selectedRows = this.selectList.getSelectedRows();
      if (selectedRows.length > 0) {
        if (!this.selectList.inSelectListEditMode) {
          const row = selectedRows[0];
          const dataView = this.selectList.getDataView(row);
          this.onRowClicked({ cell: 0, item: dataView, originalEvent: event, row });
        }
      }
    } else if (event.which === 38 || event.which === 40) { // 38 => up arrow; 40 => down arrow
      if (!this.selectList.inSelectListEditMode && !this.allowMultiSelections) {
        const cellEvent = this.selectList.getCellFromEvent(event);
        const cellEventRow = cellEvent?.row;
        if (!isNaN(cellEventRow)) {
          this.selectList.listContentComponent.unSelectAllRows();
          this.selectList.listContentComponent.setSelectedRows([ cellEventRow ], false);
        }
      }
    }
  }

  /**
   *
   * @type {EventEmitter<LMSelectionEvent>}
   */
  @Output() selectionEvent = new EventEmitter<LMSelectionEvent>();

  /**
   *
   * @type {EventEmitter<string>}
   */
  @Output() titleChanged = new EventEmitter<string>();
  @Output() navigatingToolbarEvent = new EventEmitter<LMToolbarButtonEvent>();

  @Input() listUrlInfo: LMListUrlInfo;

  /**
   * The list spec of the select list used to Select a value for our
   * select field.
   */
  @Input() listSpec: LMListSpec;

  /**
   * The SelectRequestData containing what's needed to run the select and
   * page appropriately over it's records.
   */
  @Input() selectRequestData: SelectRequestData;

  /**
   * The button ids for the done editing button
   */
  @Input() buttonIds: {doneEditing?: string};

  private executeFocus = false;
  private allowMultiSelections = false;
  private enterKeyFromFilterRow = false;
  private _selectionEvent: LMSelectionEvent;

  constructor(
    private lmContextService: LMContextService,
    private elementRef: ElementRef,
    private changeDetectionRef: ChangeDetectorRef,
    private ngZone: NgZone) {  }

  ngAfterViewInit() {
    this.allowMultiSelections = this.selectList.selectRequestData?.allowMultiSelections;
  }

  ngAfterViewChecked(): void {
    if (this.executeFocus) {
      this.executeFocus = false;
      this.handleFocus();
    }
  }

  /**
   * Re-emit the row clicked event.
   * @param listRowClickedEvent
   */
  onRowClicked(listRowClickedEvent: LMListRowClickedEvent) {
    // list is in selection mode
    if (!this.selectList.inSelectListEditMode) {
      // lookup field allows multiple selections
      if (this.allowMultiSelections) {
        // When the first row is activated (and not selected), the soho datagrid does not fire an activated
        // event if the user clicks on the activated row so no selection will take place.
        if (this.selectList.listContentComponent.activatedRow() === listRowClickedEvent.row &&
            this.selectList.listContentComponent.getSelectedRows().length === 0) {
          this.selectList.listContentComponent.setSelectedRows([ listRowClickedEvent.row ]);
        }
      } else {
        // lookup field does not allow multiple selections so return the selected row value to the lookup field
        this._selectionEvent = new LMSelectionEvent(this, [ listRowClickedEvent.row ], listRowClickedEvent.item);
        this.selectionEvent.emit(this._selectionEvent);
      }
    }
  }

  /**
   * Re-emit the row selection event.
   * @param listSelectionEvent
   */
  onRowsSelected(listSelectionEvent: LMListSelectionEvent) {
    // Handle row selection when the field allows multiple selections
    if (this.allowMultiSelections) {
      this._selectionEvent = new LMSelectionEvent(this, listSelectionEvent.selectedRows);
      this.selectionEvent.emit(this._selectionEvent);
    }
  }

  /**
   * Re-emit title changed event so that the apply function has a chance
   * to subscribe to it and in turn update the dialogs title.
   */
  onTitleChanged(newTitle: string) {
    this.titleChanged.emit(newTitle);
  }

  onToolbarButtonEvent(event: LMToolbarButtonEvent) {
    const id = event.actionReference.id.toUpperCase();
    if (id.indexOf(AppConstants.ACTION_TYPE_CREATE) > -1 ||
        id.indexOf(AppConstants.ACTION_TYPE_UPDATE) > -1 ) {
      this.navigatingToolbarEvent.emit(event);
    }
  }

  onKeyDown(event: LMListKeyDownEvent) {
    if (event instanceof KeyboardEvent) {
      if ((event as KeyboardEvent).key === 'Enter' && (event?.target as HTMLElement)?.closest('thead')) {
        this.enterKeyFromFilterRow = true;
      }
    } else {
      this.enterKeyFromFilterRow = false;
    }
  }

  onListDisplayed() {
    this.executeFocus = true;

    // TODO: CAL - hopefully temporary until get a soho enhancement for
    // https://github.com/infor-design/enterprise/issues/6620
    if (this.selectList.showMultiSelectTags) {
      this.selectList.selectionTagsComponent.setMaxWidth();
    }
  }

  triggerDetectChanges() {
    this.changeDetectionRef.markForCheck();
  }

  getSelectionEvent(): LMSelectionEvent {
    return this._selectionEvent;
  }

  handleFocus() {
    // Needs to be in a timeout to ensure that the list in the modal is displayed and has completed setting its
    // focus element before attempting to set focus on the first row in the datagrid.
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        // does the child list have a search form displayed initially or always
        if (this.selectList.hasSearchForm && this.selectList.showSearchForm) {
          this.selectList.searchFormComponent.elementRef.nativeElement.querySelectorAll('input')[0].focus();
          return;
        }

        // show the select all selection checkbox in both selection and edit mode
        if (this.selectList.showMultiSelectTags) {
          this.selectList.listContentComponent.showSelectAllCheckBox(false);
        }

        // attempt to set focus in the select list
        this.selectList.listContentComponent.setSelectListFocus();

      }, 2);
    });
  }
}
